import { useSnackbar } from "notistack";
import { useMutation } from "react-query";
import {
  coreRoutes,
  idFlavor,
  publicClientFileSubRoute,
  urlBuilder,
} from "@specsheet-common/shared-types";
import { apiWithHeaders } from "src/configuration/api";
import { useDialogContext } from "src/hooks/contexts/useDialogContext";
import { useError } from "src/hooks/utils/useError";

interface MutationProps {
  onSuccess?(): void;
  onError?(): void;
}

export const useDownloadPublicFile = ({
  onError,
  onSuccess,
}: MutationProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { onClose } = useDialogContext();
  const { errorAlert } = useError();

  const effect = async (fileId: idFlavor<"ClientFile">) => {
    const res = await apiWithHeaders.get(
      urlBuilder({
        root: coreRoutes.publicClientFile,
        sub: publicClientFileSubRoute.download,
        tailParam: fileId,
      }),
      { responseType: "blob" }
    );

    const disposition = res.headers["content-disposition"] || "";
    const filenameRegex = /filename[^\n;=]*=((["']).*?\2|[^\n;]*)/;
    const matches = filenameRegex.exec(disposition || "");
    if (matches === null || !matches[1]) {
      throw new Error("No filename");
    }
    const downloadAsFilename = matches[1].replace(/["']/g, "");

    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", downloadAsFilename); //or any other extension
    document.body.append(link);
    link.click();

    return res;
  };

  return useMutation(effect, {
    onSuccess: () => {
      enqueueSnackbar("File is ready", {
        variant: "success",
      });
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    },
    onError: (e) => {
      if (onError) {
        onError();
      }

      errorAlert(undefined, e);
    },
  });
};
